import React, { useState, useEffect } from 'react';
import { 
  View, 
  Text, 
  StyleSheet, 
  ScrollView
} from 'react-native';
import { showAlert } from '@/utils/common/alert';
import { useRoute, useNavigation } from '@react-navigation/native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { RNButton } from '@/components';
import { 
  PaymentSectionHeader,
  PaymentRadioOption,
  PaymentAmountInput,
  ConvenienceFeesSection,
  PaymentSectionDivider,
} from '@/components/residents/payments/shared';
import { useAppSelector } from '@/store';
import { PaymentMethodSelector } from '@/components/residents/payments/PaymentMethodSelector';
import { usePaymentData } from '@/features/payments/hooks/usePaymentData';
import { 
  useSetupAutoPayMutation,
} from '@/store/api';
import { sanitizeCurrencyInput, getFirstOfNextMonth } from '@/utils/common/formatting';
import { PaymentModal, PaymentModalContent } from '@/components/residents/payments';

type Params = {
  leaseId: number;
  residentId?: number;
  fromTransactionReceived?: boolean;
};

export enum AUTOPAY_WIZARD_STEPS {
  SETTINGS = 0,        // Amount, Date, Payment Method all in one (like web)
  PROCESSING = 1,      // Processing step  
  SUCCESS = 2,         // Success step
}

export const AutoPayWizardScreen: React.FC = () => {
  const route = useRoute<any>();
  const navigation = useNavigation<any>();
  
  const { leaseId, residentId, fromTransactionReceived } = (route.params || {}) as Params;
  const [currentStep, setCurrentStep] = useState<AUTOPAY_WIZARD_STEPS>(AUTOPAY_WIZARD_STEPS.SETTINGS);
  
  const authUser = useAppSelector((s) => (s as any).auth?.user);
  
  // Form state - matching web app structure
  const [amountOption, setAmountOption] = useState<'full' | 'custom'>('full');
  const [customAmount, setCustomAmount] = useState<string>('');
  const [dateRangeOption, setDateRangeOption] = useState<'default' | 'custom'>('default');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<any>(null);
  
  const [setupAutoPay, { isLoading: isSettingUp }] = useSetupAutoPayMutation();

  // Get payment data - reuse existing hook
  const paymentData = usePaymentData(residentId);
  const { 
    paymentMethods, 
    balanceDue, 
    feeStructureDisplay,
    selectedPaymentMethod: currentSelectedMethod,
    updateSelectedPaymentMethod,
  } = paymentData;

  const property = useAppSelector((s) => (s as any).property?.current);
  const minPaymentAmount = Number(property?.minPaymentAmount ?? 10);

  // Initialize selected payment method from existing payment data
  useEffect(() => {
    if (currentSelectedMethod && !selectedPaymentMethod) {
      setSelectedPaymentMethod(currentSelectedMethod);
    } else if (paymentMethods?.length > 0 && !selectedPaymentMethod) {
      const firstMethod = paymentMethods[0];
      setSelectedPaymentMethod(firstMethod);
      updateSelectedPaymentMethod(firstMethod);
    }
  }, [paymentMethods, selectedPaymentMethod, currentSelectedMethod, updateSelectedPaymentMethod]);

  // Initialize custom amount - matching Payment Summary logic
  useEffect(() => {
    if (!customAmount) {
      const fallbackMin = minPaymentAmount;
      const canPayBalanceUnderMin = balanceDue > 0 && balanceDue < minPaymentAmount;
      const defaultCustom = canPayBalanceUnderMin && balanceDue > 0 ? balanceDue : fallbackMin;
      const formattedAmount = String(Number.isFinite(defaultCustom) ? defaultCustom.toFixed(2) : '');
      setCustomAmount(formattedAmount);
    }
  }, [balanceDue, customAmount, minPaymentAmount]);

  // Handle amount option changes - matching Payment Summary logic
  const handleFullBalanceAmount = () => {
    setAmountOption('full');
  };

  const handleOtherAmount = () => {
    setAmountOption('custom');
  };

  // Handle custom amount text changes
  const handleCustomAmountChange = (text: string) => {
    const cleanedText = sanitizeCurrencyInput(text);
    setCustomAmount(cleanedText);
  };

  const handleSetupAutoPay = async () => {
    if (!selectedPaymentMethod) return;
    
    setCurrentStep(AUTOPAY_WIZARD_STEPS.PROCESSING);
    
    try {
      const setupData = {
        leaseId,
        sourceId: Number(selectedPaymentMethod.sourceId || selectedPaymentMethod.id),
        residentId: residentId && residentId !== authUser?.id ? residentId : undefined,
        autopayIsFullAmount: amountOption === 'full',
        autopayIsLeaseLifetime: dateRangeOption === 'default',
        ...(amountOption === 'custom' && { fixedAmount: Number(customAmount) }),
      };

      await setupAutoPay(setupData).unwrap();
      setCurrentStep(AUTOPAY_WIZARD_STEPS.SUCCESS);
    } catch (error) {
      console.error('AutoPay setup error:', error);
      showAlert('Error', 'Failed to set up AutoPay. Please try again.');
      setCurrentStep(AUTOPAY_WIZARD_STEPS.SETTINGS);
    }
  };

  const handleClose = () => {
    if (fromTransactionReceived) {
      // Go back to payments screen after coming from transaction received
      navigation.replace('Payments');
    } else {
      navigation.goBack();
    }
  };

  const canProceedToPaymentMethod = () => {
    if (amountOption === 'custom') {
      const amount = Number(customAmount);
      return amount >= minPaymentAmount;
    }
    return true;
  };

  const canProceedToConfirmation = () => {
    return selectedPaymentMethod !== null;
  };

  // getFirstOfNextMonth now provided by shared utils

  // Format date range text to match web app exactly
  const getDateRangeText = () => {
    const now = new Date();
    const firstOfNextMonth = getFirstOfNextMonth(now);
    const formattedDate = firstOfNextMonth.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit", 
      year: "numeric",
    });
    return `From the first of the month (${formattedDate}) to lease end`;
  };

  // Step 1: AutoPay Settings (Amount, Date Range, Payment Method) - matching web app
  const renderSettingsStep = () => (
    <ScrollView style={styles.stepContainer}>
      <View style={styles.section}>
        <PaymentSectionHeader 
          title="Amount"
          tooltipText="There is a limit of one autopay per user per lease. The full balance autopay will not process a payment if you have a credit on your account or if your balance owed is equal to $0. Custom amounts will process the specified amount if the balance owed is greater than $0 and will result a credit on your account if the amount is greater than the balance owed."
        />

        <PaymentRadioOption
          selected={amountOption === 'full'}
          onPress={handleFullBalanceAmount}
          label="Full balance"
        />

        <PaymentRadioOption
          selected={amountOption === 'custom'}
          onPress={handleOtherAmount}
          label="Other amount"
          rightComponent={
            <PaymentAmountInput
              value={customAmount}
              onChangeText={handleCustomAmountChange}
              placeholder="10.00"
              editable={amountOption === 'custom'}
              disabled={amountOption !== 'custom'}
            />
          }
        />
      </View>

      <PaymentSectionDivider />

      <View style={styles.section}>
        <PaymentSectionHeader 
          title="Date range"
          tooltipText="Your payments will be processed on the first day of each month until your lease ends."
        />
        
        <PaymentRadioOption
          selected={dateRangeOption === 'default'}
          onPress={() => setDateRangeOption('default')}
          label={getDateRangeText()}
        />
      </View>

      <PaymentSectionDivider />

      {/* Payment Method Section - matching web app structure */}
      <PaymentMethodSelector
        paymentMethods={paymentMethods}
        selectedMethod={selectedPaymentMethod || currentSelectedMethod}
        onSelectMethod={(method) => {
          setSelectedPaymentMethod(method);
          updateSelectedPaymentMethod(method);
        }}
        onManagePaymentMethods={() => {
          // Match PaymentSummaryModal navigation pattern
          navigation.replace('AddOrChangePaymentMethod', { 
            leaseId, 
            residentId,
            returnTo: 'AutoPayWizard',
            successPage: 'AutoPayWizard',
            managePaymentPage: 'ManageMyWallet'
          });
        }}
        isLoading={false}
        showChangeButton={true}
        maxHeight={240}
      />

      <PaymentSectionDivider />

      <ConvenienceFeesSection feeStructure={feeStructureDisplay} />
    </ScrollView>
  );


  // Step 2: Processing
  const renderProcessingStep = () => (
    <View style={styles.centerContainer}>
      <LoadingIcon />
      <Text style={styles.processingTitle}>Setting up AutoPay...</Text>
      <Text style={styles.processingMessage}>
        Please wait while we configure your recurring payment.
      </Text>
    </View>
  );

  // Step 3: Success
  const renderSuccessStep = () => (
    <View style={styles.centerContainer}>
      <SuccessIcon />
      <Text style={styles.successTitle}>AutoPay Set Up Successfully!</Text>
      <Text style={styles.successMessage}>
        Your recurring payment has been configured. You'll be charged automatically 
        on the first of each month.
      </Text>
    </View>
  );

  const getStepTitle = () => {
    switch (currentStep) {
      case AUTOPAY_WIZARD_STEPS.SETTINGS:
        return 'Autopay settings';
      case AUTOPAY_WIZARD_STEPS.PROCESSING:
        return 'Setting up AutoPay';
      case AUTOPAY_WIZARD_STEPS.SUCCESS:
        return 'AutoPay configured';
      default:
        return 'AutoPay Setup';
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case AUTOPAY_WIZARD_STEPS.SETTINGS:
        return renderSettingsStep();
      case AUTOPAY_WIZARD_STEPS.PROCESSING:
        return renderProcessingStep();
      case AUTOPAY_WIZARD_STEPS.SUCCESS:
        return renderSuccessStep();
      default:
        return null;
    }
  };

  const renderFooter = () => {
    if (currentStep === AUTOPAY_WIZARD_STEPS.PROCESSING) {
      return null;
    }

    if (currentStep === AUTOPAY_WIZARD_STEPS.SUCCESS) {
      return (
        <View style={styles.successFooter}>
          <RNButton 
            title="Close" 
            variant="primary" 
            onPress={handleClose}
            textStyle={styles.confirmBtnText}
          />
        </View>
      );
    }

    return (
      <View style={styles.footerRow}>
        {currentStep === AUTOPAY_WIZARD_STEPS.SETTINGS && (
          <>
            <RNButton 
              title="Cancel" 
              variant="outlined" 
              onPress={handleClose}
              style={styles.cancelBtn}
              textStyle={styles.cancelBtnText}
            />
            <RNButton 
              title="Confirm" 
              variant="primary" 
              onPress={handleSetupAutoPay}
              disabled={!canProceedToPaymentMethod() || !canProceedToConfirmation() || isSettingUp}
              textStyle={styles.confirmBtnText}
            />
          </>
        )}
      </View>
    );
  };

  return (
    <PaymentModal 
      title={getStepTitle()} 
      onClose={handleClose} 
      width={600}
      showCloseButton={currentStep !== AUTOPAY_WIZARD_STEPS.PROCESSING}
    >
      <PaymentModalContent>
        {renderStepContent()}
        {renderFooter()}
      </PaymentModalContent>
    </PaymentModal>
  );
};

// Helper Icons
const SuccessIcon: React.FC = () => {
  const Icon = (RESIDENTS_SVG_COMPONENTS as any).CHECK_CIRCLE;
  return Icon ? (
    <Icon width={64} height={64} color="#10B981" />
  ) : (
    <View style={styles.successIconFallback}>
      <Text style={styles.successIconText}>✓</Text>
    </View>
  );
};

const LoadingIcon: React.FC = () => {
  const Icon = (RESIDENTS_SVG_COMPONENTS as any).LOADING_SPINNER;
  return Icon ? (
    <Icon width={48} height={48} color={RESIDENTS_DESKTOP_COLORS.BUTTON_PRIMARY} />
  ) : (
    <View style={styles.loadingIconFallback}>
      <Text style={styles.loadingIconText}>⟳</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  stepContainer: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
  },
  section: {
    paddingVertical: 14,
  },
  processingTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    marginTop: 24,
    marginBottom: 8,
  },
  processingMessage: {
    fontSize: 16,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
  },
  successTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    marginTop: 24,
    marginBottom: 8,
    textAlign: 'center',
  },
  successMessage: {
    fontSize: 16,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    lineHeight: 24,
  },
  successIconFallback: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#10B981',
    alignItems: 'center',
    justifyContent: 'center',
  },
  successIconText: {
    fontSize: 32,
    fontWeight: 'bold',
    color: 'white',
  },
  loadingIconFallback: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.BUTTON_PRIMARY,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingIconText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: 'white',
  },
  footerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
  },
  successFooter: {
    alignItems: 'center',
    marginTop: 16,
  },
  cancelBtn: {
    borderColor: RESIDENTS_DESKTOP_COLORS.BUTTON_SECONDARY,
  },
  cancelBtnText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
  confirmBtnText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
});

export default AutoPayWizardScreen;